import React from 'react';
import styled, { css } from 'styled-components';
import { surface, canvas, textPrimary, textDisabled, borderDefault, info, infoLight } from '../../tokens/colors';
import { radiusMd } from '../../tokens/radii';
import { fontFamily } from '../../tokens/typography';

const baseInput = css<{ $error?: boolean }>`
  padding: 7px 10px;
  border: 0.5px solid ${({ $error }) => $error ? '#F09595' : borderDefault};
  border-radius: ${radiusMd};
  font-family: ${fontFamily};
  font-size: 13px;
  color: ${textPrimary};
  background: ${surface};
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.1s, box-shadow 0.1s;

  &:focus {
    border-color: ${({ $error }) => $error ? '#F09595' : info};
    box-shadow: 0 0 0 3px ${({ $error }) => $error ? '#FCEBEB' : infoLight};
  }

  &:disabled {
    background: ${canvas};
    color: ${textDisabled};
    cursor: not-allowed;
  }

  &::placeholder { color: #C4C3BE; }
`;

export const StyledInput    = styled.input<{ $error?: boolean }>`${baseInput}`;
export const StyledSelect   = styled.select<{ $error?: boolean }>`${baseInput} cursor: pointer;`;
export const StyledTextarea = styled.textarea<{ $error?: boolean }>`${baseInput} resize: vertical; min-height: 80px;`;

// ── TimeInput — campo com máscara HH:MM ──────────────────────────────────────

interface TimeInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (v: string) => void;
  error?: boolean;
}

function applyTimeMask(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

export const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, error, ...rest }) => (
  <StyledInput
    type="text"
    $error={error}
    value={value}
    maxLength={5}
    placeholder="00:00"
    style={{ width: 82, textAlign: 'center', letterSpacing: '0.04em' }}
    onChange={e => onChange(applyTimeMask(e.target.value))}
    {...rest}
  />
);

// ── ColorInput — input de cor com preview ────────────────────────────────────

interface ColorInputProps {
  value: string;
  onChange: (v: string) => void;
  label?: string;
}

export const ColorInput: React.FC<ColorInputProps> = ({ value, onChange, label }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    {label && <span style={{ fontSize: 11, fontWeight: 600, color: '#5C5C58' }}>{label}</span>}
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ width: 36, height: 32, padding: 2, border: `0.5px solid ${borderDefault}`, borderRadius: radiusMd, cursor: 'pointer' }}
      />
      <span style={{ fontSize: 11, color: '#9C9C96', fontFamily: 'monospace' }}>{value}</span>
    </div>
  </div>
);
